import React, { FC } from 'react';
import { Canvas, Circle, Path, PencilBrush, Text } from 'fabric';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Button,
  ButtonsContainer,
  DrawSection,
  SegmentationCanvasContainer,
} from './SegmentationCanvas.style';
import { labelToColor } from '../../../../../utilities/labelColors';

const getInitials = label => {
  if (!label) return '';
  return label
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('');
};

function installVertexModifiedHandler(
  canvas: Canvas,
  setActiveSegmentation: any,
  activeSegmentation: any,
  vertexCirclesRef: any
) {
  canvas.on('object:modified', e => {
    const o = e.target as Circle & { __meta?: VertexHandleMeta };
    if (!o || !isVertexObject(o)) return;

    const { pathObj } = o.__meta;

    // Remove all related vertex circles
    const relatedCircles = vertexCirclesRef.current.filter(
      c => isVertexObject(c) && c.__meta.pathObj === pathObj
    );

    relatedCircles.forEach(c => canvas.remove(c));
    vertexCirclesRef.current = vertexCirclesRef.current.filter(c => !relatedCircles.includes(c));

    // Add updated vertex circles
    const newCircles = addVertexCirclesForPath(canvas, pathObj);
    vertexCirclesRef.current.push(...newCircles);

    // Update segmentation state
    setActiveSegmentation({
      ...activeSegmentation,
      paths: canvas
        .getObjects()
        .filter(o => o.type === 'path')
        .map(o => (o as Path).path?.join('')),
      dataUrl: canvas.toDataURL({
        format: 'png',
        multiplier: 1,
      }),
      isLabeled: true,
      isFullyVisible: true,
    });
  });
}

function installVertexMoveHandler(canvas: Canvas, vertexCirclesRef: any) {
  canvas.on('object:moving', e => {
    const o = e.target as Circle & { __meta?: VertexHandleMeta };
    if (!o || !isVertexObject(o)) return;

    const { pathObj } = o.__meta;

    // Get all vertices for this path
    const relatedCircles = vertexCirclesRef.current.filter(
      c => isVertexObject(c) && c.__meta.pathObj === pathObj
    );

    const sorted = relatedCircles
      .filter(c => c.__meta?.vertexIndex != null)
      .sort((a, b) => a.__meta.vertexIndex - b.__meta.vertexIndex);

    // Update path directly
    const newPath = sorted.map((circle, i) => {
      if (i === 0) return ['M', circle.left, circle.top];
      return ['Q', circle.left, circle.top, circle.left, circle.top]; // dummy control = end point
    });
    newPath.push(['Z']);

    pathObj.set({ path: newPath });
    canvas.requestRenderAll();
  });
}

type VertexHandleMeta = {
  pathObj: Path;
  segIndex: number;
  coordIndices: number[]; // which indices within the segment to update (e.g., [1,2] or [3,4])
  vertexIndex: number;
};

const isVertexObject = (o: any): o is Circle & { __meta: VertexHandleMeta } => !!o.__meta;

function makeVertexCircle(
  x: number,
  y: number,
  meta: VertexHandleMeta
): Circle & { __meta: VertexHandleMeta } {
  const c = new Circle({
    left: x,
    top: y,
    radius: 4,
    fill: '#ffffff',
    stroke: '#03a9f4',
    strokeWidth: 2,
    originX: 'center',
    originY: 'center',
    hasControls: false,
    hasBorders: false,
    selectable: true,
    evented: true,
  }) as Circle & { __meta: VertexHandleMeta };

  c.__meta = meta;
  return c;
}

function addVertexCirclesForPath(canvas: Canvas, pathObj: Path, minDistance = 15) {
  const verts: (Circle & { __meta: VertexHandleMeta & { vertexIndex: number } })[] = [];
  const segments = pathObj.path as any[];

  let vertexIndex = 0;
  const distance = (x1: number, y1: number, x2: number, y2: number) => Math.hypot(x2 - x1, y2 - y1);
  const addedPoints: { x: number; y: number }[] = [];

  const tryAddVertex = (x: number, y: number, segIndex: number, coordIndices: number[]) => {
    const isFarEnough = addedPoints.every(p => distance(p.x, p.y, x, y) >= minDistance);

    if (isFarEnough || vertexIndex === 0) {
      const circle = makeVertexCircle(x, y, {
        pathObj,
        segIndex,
        coordIndices,
        vertexIndex,
      });
      verts.push(circle);
      addedPoints.push({ x, y });
      vertexIndex += 1;
    }
  };

  segments.forEach((seg, i) => {
    const cmd = seg[0];
    if (cmd === 'M' || cmd === 'L') {
      tryAddVertex(seg[1], seg[2], i, [1, 2]);
    } else if (cmd === 'Q') {
      tryAddVertex(seg[1], seg[2], i, [1, 2]); // control point (optional)
      tryAddVertex(seg[3], seg[4], i, [3, 4]); // end point
    } else if (cmd === 'C') {
      tryAddVertex(seg[1], seg[2], i, [1, 2]); // control 1
      tryAddVertex(seg[3], seg[4], i, [3, 4]); // control 2
      tryAddVertex(seg[5], seg[6], i, [5, 6]); // end
    }
  });

  verts.forEach(v => canvas.add(v));
  return verts;
}

const visualizeSegmentation = (
  fabricRef,
  allSegmentationPredictions,
  showSegmentationPredictions,
  allSegmentationReferences,
  showSegmentationReferences,
  activeSegmentationClass,
  updateModifications,
  setSegmentationObjectToBeAdopted,
  activeSegmentationPredictionClasses,
  activeSegmentationReferenceClasses,
  activeOtherSegmentationClasses,
  showOtherSegmentations,
  otherSegmentations
) => {
  if (!fabricRef.current) return;

  setSegmentationObjectToBeAdopted(null);

  const pathOptions = {
    strokeWidth: 2,
    fill: '#49495c',
    color: '#03a9f4',
    lockMovementX: true,
    lockMovementY: true,
    opacity: 0.45,
    selectable: false,
    evented: false,
  };

  const createText = (centerX, centerY, label) =>
    new Text(label, {
      left: centerX,
      top: centerY,
      fontSize: 12,
      fill: '#ffffff',
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
      opacity: 0.8,
      fontFamily: 'arial',
      objectType: 'text',
    });

  const drawPathsWithLabels = (pathStrings, label, objectType) => {
    const color = labelToColor(label);

    const initials = getInitials(label);

    const paths = pathStrings?.map(
      pathString => new Path(pathString, { ...pathOptions, fill: color, stroke: color, objectType })
    );

    paths?.forEach(path => {
      fabricRef.current.add(path);

      const bounds = path.getBoundingRect();
      const centerX = bounds.left + bounds.width / 2;
      const centerY = bounds.top + bounds.height / 2;
      fabricRef.current.add(createText(centerX, centerY, initials));
    });
  };

  // labels
  if (showOtherSegmentations) {
    otherSegmentations
      ?.filter(segmentation =>
        activeOtherSegmentationClasses?.includes(segmentation.segmentationClassValue)
      )
      ?.forEach(label => drawPathsWithLabels(label.paths, label.segmentationClassValue, 'label'));
  }

  // predictions
  if (showSegmentationPredictions) {
    allSegmentationPredictions
      ?.filter(prediction =>
        activeSegmentationPredictionClasses?.includes(prediction.segmentationClassValue)
      )
      ?.forEach(prediction => {
        drawPathsWithLabels(prediction.paths, prediction.segmentationClassValue, 'prediction');
      });

    const activePrediction = activeSegmentationClass
      ? allSegmentationPredictions?.find(
          pred => pred.segmentationClassValue === activeSegmentationClass.value
        )
      : undefined;

    if (activePrediction) {
      setSegmentationObjectToBeAdopted({
        ...activePrediction,
        dataUrl: fabricRef.current.toDataURL({ format: 'png' }),
      });
    }
  }

  // references
  if (showSegmentationReferences) {
    allSegmentationReferences
      ?.filter(reference =>
        activeSegmentationReferenceClasses?.includes(reference.segmentationClassValue)
      )
      ?.forEach(reference => {
        drawPathsWithLabels(reference.paths, reference.segmentationClassValue, 'reference');
      });
    const activeReference = activeSegmentationClass
      ? allSegmentationReferences?.find(
          ref => ref.segmentationClassValue === activeSegmentationClass.value
        )
      : undefined;

    if (activeReference) {
      setSegmentationObjectToBeAdopted(activeReference);
    }
  }

  fabricRef.current.freeDrawingBrush = new PencilBrush(fabricRef.current);
  fabricRef.current.freeDrawingBrush.color = '#03a9f4';
  fabricRef.current.freeDrawingBrush.width = 1;
  fabricRef.current.freeDrawingBrush.strokeDashArray = [10];
  fabricRef.current.freeDrawingBrush.fill = '#ff000082';
  fabricRef.current.freeDrawingBrush.id = 'pencil';
};

// Function to create a straight line path between the end of one path and the start of another
export function createLineTo(path) {
  const firstCommand2 = path[0];
  const [__, x2, y2] = firstCommand2; // Start point of path2
  return [['L', x2, y2]];
}

//iterate through each adjacent pair of paths and produce a straight line path between each
export function connectPath(fabricObjects) {
  if (!fabricObjects || fabricObjects.length === 0) {
    return undefined;
  }
  const currentPath = fabricObjects[0].path;
  const connectorPaths = currentPath;

  for (let i = 1; i < fabricObjects.length; i++) {
    const nextPath = fabricObjects[i].path;
    if (nextPath[0][0] === 'M') {
      nextPath[0][0] = 'L';
    } else {
      connectorPaths.push(...createLineTo(nextPath));
    }
    connectorPaths.push(...nextPath);
  }
  //final line to close geometry
  connectorPaths.push(...createLineTo(fabricObjects[0].path));

  return connectorPaths;
}

interface IProps {
  activeFrameIndex: any;
  activeSegmentation: any;
  allSegmentations: any;
  allSegmentationPredictions: any;
  allSegmentationReferences: any;
  setActiveSegmentation: any;
  dicom: any;
  scale: any;
  useOpenContours: any;
  showOtherSegmentations: any;
  showSegmentationPredictions: any;
  showSegmentationReferences: any;
  activeSegmentationClass: any;
  setSegmentationObjectToBeAdopted: any;
  editMode: boolean;
  activeSegmentationPredictionClasses: any;
  activeSegmentationReferenceClasses: any;
  activeOtherSegmentationClasses: any;
}

const SegmentationCanvas: FC<IProps> = props => {
  const {
    activeFrameIndex,
    activeSegmentation,
    allSegmentations,
    allSegmentationPredictions,
    allSegmentationReferences,
    setActiveSegmentation,
    dicom,
    scale,
    useOpenContours,
    showOtherSegmentations,
    showSegmentationPredictions,
    showSegmentationReferences,
    activeSegmentationClass,
    setSegmentationObjectToBeAdopted,
    editMode,
    activeSegmentationPredictionClasses,
    activeSegmentationReferenceClasses,
    activeOtherSegmentationClasses,
  } = props;
  const fabricRef = useRef<any>(null);
  const [_, setActiveMode] = useState('draw');

  const vertexCirclesRef = useRef<any[]>([]);

  const [history, setHistory] = useState([]);

  const updateModifications = useCallback(
    savehistory => {
      if (savehistory === true) {
        const myjson = JSON.stringify(fabricRef.current);
        const origin = JSON.parse(JSON.stringify(history));
        origin.push(myjson);
        setHistory(origin);
      }
    },
    [history]
  );

  useEffect(() => {
    fabricRef.current = new Canvas('Draw', {
      erasable: true,
      isDrawingMode: !editMode,
      selection: false,
      enableRetinaScaling: true,
      renderOnAddRemove: true,
      // Add touch support
      enableTouchEvents: true,
      touchEventDelay: 0,
      // Configure for better stylus support
      perPixelTargetFind: true,
      skipTargetFind: false,
    });
    fabricRef.current.counter = 0;

    const otherSegmentations = allSegmentations?.filter(
      segmentation => segmentation?.segmentationClass !== activeSegmentation?.segmentationClass
    );

    visualizeSegmentation(
      fabricRef,
      allSegmentationPredictions,
      showSegmentationPredictions,
      allSegmentationReferences,
      showSegmentationReferences,
      activeSegmentationClass,
      updateModifications,
      setSegmentationObjectToBeAdopted,
      activeSegmentationPredictionClasses,
      activeSegmentationReferenceClasses,
      activeOtherSegmentationClasses,
      showOtherSegmentations,
      otherSegmentations
    );

    const pathStrings = activeSegmentation?.paths;

    const otherStrings = otherSegmentations?.map(segmentation => segmentation.paths);

    const paths = pathStrings?.map(
      pathString =>
        new Path(pathString, {
          fill: '#ff000082',
          strokeWidth: 2,
          opacity: 0.8,
          lockMovementX: true,
          lockMovementY: true,
          selectable: false,
          objectType: 'activeLabel',
        })
    );

    const otherPaths: Path[] = [];
    otherStrings?.forEach(pathString => {
      pathString?.map(p =>
        otherPaths.push(
          new Path(p, {
            fill: '#49495c',
            strokeWidth: 2,
            opacity: 0.6,
            objectType: 'label',
          })
        )
      );
    });

    paths?.forEach(path => fabricRef.current.add(path));

    fabricRef.current.freeDrawingBrush = new PencilBrush(fabricRef.current);
    fabricRef.current.freeDrawingBrush.color = '#03a9f4';
    fabricRef.current.freeDrawingBrush.width = 1;
    fabricRef.current.freeDrawingBrush.strokeDashArray = [10];
    fabricRef.current.freeDrawingBrush.fill = '#ff000082';
    fabricRef.current.freeDrawingBrush.id = 'pencil';
    fabricRef.current.on('object:modified', function () {
      updateModifications(true);
    });

    fabricRef.current.on('mouse:up', function () {
      if (!fabricRef.current.freeDrawingBrush) {
        return;
      }
      if (!useOpenContours) {
        const objects = fabricRef.current.getObjects();
        const last = objects[objects.length - 1];

        // only do this if we were drawing, not erasing
        if (fabricRef.current.freeDrawingBrush.id !== 'eraseMode' && last && last.type === 'path') {
          // 1. tag it so future logic can find it
          last.objectType = 'activeLabel';

          // 2. close the path so fill can work
          const p = last.path;
          if (p && p.length > 0) {
            const [, startX, startY] = p[0]; // usually ['M', x, y]
            // ensure it ends at start and has Z
            p.push(['L', startX, startY]);
            p.push(['Z']);
            last.set({ path: p });
          }

          // 3. make it a blob (fill + stroke red)
          last.set({
            fill: '#ff0000c4',
            stroke: '#ff0000c4',
            strokeDashArray: [],
            erasable: true,
            selectable: true,
            lockMovementX: true,
            lockMovementY: true,
          });

          fabricRef.current.requestRenderAll();
        }

        const newSegmentation = {
          ...activeSegmentation,
          paths: fabricRef.current
            .getObjects()
            .filter(o => o.objectType === 'activeLabel')
            .map(o => o.path?.join('')),
          dataUrl: fabricRef.current.toDataURL({ format: 'png' }),
          isLabeled: true,
          isFullyVisible: true,
          dicom: dicom?.uuid,
        };

        setActiveSegmentation(newSegmentation);
      } else {
        // Open Contour mode
        if (fabricRef.current.freeDrawingBrush.id !== 'eraseMode') {
          fabricRef.current.getObjects().forEach(o => {
            if (o.objectType === 'activeLabel') {
              o.strokeDashArray = [];
              o.stroke = '#ff0000c4';
              o.erasable = true;
              o.selectable = true;
            }
          });
        }
      }
    });

    fabricRef.current.on('mouse:moving', function () {
      if (fabricRef.current.freeDrawingBrush.id !== 'eraseMode') {
        fabricRef.current.getObjects().forEach(o => {
          if (o.objectType === 'activeLabel') {
            o.fill = '#ff0000c4';
            o.erasable = true;
          }
        });
      }
    });

    fabricRef.current.setWidth(dicom?.pixelWidth);
    fabricRef.current.setHeight(dicom?.pixelHeight);

    return () => fabricRef.current.dispose();
  }, [
    dicom?.pixelWidth,
    dicom?.pixelHeight,
    activeSegmentation,
    updateModifications,
    setActiveSegmentation,
    useOpenContours,
    showOtherSegmentations,
    showSegmentationPredictions,
    showSegmentationReferences,
    allSegmentations,
    activeSegmentationClass,
    activeSegmentationPredictionClasses,
    activeSegmentationReferenceClasses,
    activeOtherSegmentationClasses,
    allSegmentationPredictions,
    allSegmentationReferences,
    editMode,
    activeFrameIndex,
    showOtherSegmentations,
  ]);

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    vertexCirclesRef.current.forEach(v => canvas.remove(v));
    vertexCirclesRef.current = [];

    const editablePaths = canvas.getObjects().filter(o => o.objectType === 'activeLabel') as Path[];

    if (editMode) {
      canvas.isDrawingMode = false;

      editablePaths.forEach(p => {
        const verts = addVertexCirclesForPath(canvas, p);
        vertexCirclesRef.current.push(...verts);
      });

      installVertexMoveHandler(canvas, vertexCirclesRef);
      installVertexModifiedHandler(
        canvas,
        setActiveSegmentation,
        activeSegmentation,
        vertexCirclesRef
      );
    } else {
      canvas.isDrawingMode = true;
    }

    return () => {
      vertexCirclesRef.current.forEach(v => canvas.remove(v));
      vertexCirclesRef.current = [];
      canvas.off('object:moving');
      canvas.off('object:modified');
    };
  }, [editMode, activeSegmentation, dicom?.uuid]);

  const handleCompleteContour = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    // 1. Find the thing the user actually drew.
    // First try objects we explicitly tagged as 'activeLabel'.
    let activeObjs = canvas.getObjects().filter(o => o.objectType === 'activeLabel');

    // Fallback: if we didn't tag anything yet (common if we never set objectType),
    // grab the LAST Path on the canvas and treat that as the active contour.
    if (activeObjs.length === 0) {
      const all = canvas.getObjects();
      const last = all[all.length - 1];
      if (last && last.type === 'path') {
        // tag it now so future logic works
        last.objectType = 'activeLabel';
        activeObjs = [last];
      }
    }

    if (activeObjs.length === 0) {
      // still nothing? nothing to finalize
      console.warn('[handleCompleteContour] no active contour found');
      return;
    }

    // 2. Pick the main contour (first active draw for now)
    const shape = activeObjs[0];

    // 3. Ensure shape.path exists and is an array of fabric commands
    if (!Array.isArray(shape.path) || shape.path.length === 0) {
      console.warn('[handleCompleteContour] shape has no path data');
    } else {
      // We will explicitly close it so fill can render as a blob.
      const pathArray = shape.path;

      // Get starting point from the first command (usually ['M', x, y])
      const firstCmd = pathArray[0];
      const startX = firstCmd[1];
      const startY = firstCmd[2];

      // Check if it's already closed with a 'Z'
      const lastCmd = pathArray[pathArray.length - 1];
      const alreadyClosed = lastCmd && lastCmd[0] === 'Z';

      if (!alreadyClosed) {
        // Add line back to start, then close
        pathArray.push(['L', startX, startY]);
        pathArray.push(['Z']);
        shape.set({ path: pathArray });
      }
    }

    // 4. Style the finalized blob (fill red, stroke red, lock it)
    // Only do this if we're not erasing right now
    if (canvas.freeDrawingBrush?.id !== 'eraseMode') {
      shape.set({
        fill: '#ff0000c4',
        stroke: '#ff0000c4',
        strokeDashArray: [],
        erasable: true,
        selectable: true,
        lockMovementX: true,
        lockMovementY: true,
      });
    }

    // 5. Remove every other "activeLabel" contour except this one,
    // so we end up with just one closed region.
    activeObjs.slice(1).forEach(obj => {
      canvas.remove(obj);
    });

    // 6. Also remove any non-path stuff we don't want baked into the final snapshot.
    // BUT do NOT clear the canvas completely because that wipes brush config etc.
    // Instead: remove everything that's not our final shape.
    canvas.getObjects().forEach(obj => {
      if (obj !== shape && obj.objectType !== 'text') {
        canvas.remove(obj);
      }
    });

    // Make sure the finalized shape is on canvas (in case we removed it by accident in cleanup)
    if (!canvas.getObjects().includes(shape)) {
      canvas.add(shape);
    }

    // Re-render after edits
    canvas.requestRenderAll();

    // 7. Persist segmentation state back up
    const activeLabelObjects = canvas
      .getObjects()
      .filter(o => o.type === 'path' && o.objectType === 'activeLabel');

    const newSegmentation = {
      ...activeSegmentation,
      paths: activeLabelObjects.map(o => o.path?.join('')),
      dataUrl: canvas.toDataURL({ format: 'png' }),
      isLabeled: true,
      isFullyVisible: true,
    };

    setActiveSegmentation(newSegmentation);
    updateModifications(true);
  };

  useEffect(() => setActiveMode('draw'), [activeFrameIndex]);

  return (
    <SegmentationCanvasContainer scale={scale}>
      <DrawSection />
      <ButtonsContainer>
        {useOpenContours && <Button onClick={handleCompleteContour}>Complete Contour</Button>}
      </ButtonsContainer>
    </SegmentationCanvasContainer>
  );
};

export default SegmentationCanvas;
